import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { IconName } from '@cbhq/cds-common';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { Tray } from '@cbhq/cds-web/overlays/tray/Tray';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

import type { AuditLogRowEntry } from '../types';
import {
  formatAuditLogDevice,
  formatAuditLogLocation,
  formatAuditLogUserAgent,
  getAuditEventMeta,
} from '../utils/auditLogDisplay';
import { truncateEventId } from '../utils/helpers';

import { AuditDetailHeader } from './AuditDetailHeader';
import { AuditDetailTraySkeleton } from './AuditDetailTraySkeleton';
import { AuditLogActorSection } from './AuditLogActorSection';
import type { AuditLogDetailRowProps } from './AuditLogDetailRow';
import { AuditLogDetailRow } from './AuditLogDetailRow';
import { AuditLogDetailSection } from './AuditLogDetailSection';

const TRAY_STYLES = {
  container: { width: '532px' },
  content: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
  },
} as const;

const messages = defineMessages({
  accessibilityLabel: {
    id: 'Activity.auditDetailTray.accessibilityLabel',
    defaultMessage: 'Audit log details',
    description: 'Accessibility label for the audit detail tray',
  },
  closeAccessibilityLabel: {
    id: 'Activity.auditDetailTray.closeAccessibilityLabel',
    defaultMessage: 'Close audit log details',
    description: 'Accessibility label for closing the audit detail tray',
  },
  previousAccessibilityLabel: {
    id: 'Activity.auditDetailTray.previousAccessibilityLabel',
    defaultMessage: 'Previous audit log',
    description: 'Accessibility label for the previous audit log button',
  },
  nextAccessibilityLabel: {
    id: 'Activity.auditDetailTray.nextAccessibilityLabel',
    defaultMessage: 'Next audit log',
    description: 'Accessibility label for the next audit log button',
  },
  resultCount: {
    id: 'Activity.auditDetailTray.resultCount',
    defaultMessage: '{currentPosition} of {totalCount} results',
    description: 'Static audit detail tray result position label',
  },
  details: {
    id: 'Activity.auditDetailTray.details',
    defaultMessage: 'Details',
    description: 'Title for the audit detail rows section',
  },
  action: {
    id: 'Activity.auditDetailTray.action',
    defaultMessage: 'Action',
    description: 'Label for the audit action detail row',
  },
  target: {
    id: 'Activity.auditDetailTray.target',
    defaultMessage: 'Target',
    description: 'Label for the audit target detail row',
  },
  deviceAndNetwork: {
    id: 'Activity.auditDetailTray.deviceAndNetwork',
    defaultMessage: 'Device & network',
    description: 'Title for the audit device and network rows section',
  },
  device: {
    id: 'Activity.auditDetailTray.device',
    defaultMessage: 'Device',
    description: 'Label for the audit device detail row',
  },
  ip: {
    id: 'Activity.auditDetailTray.ip',
    defaultMessage: 'IP',
    description: 'Label for the audit IP detail row',
  },
  location: {
    id: 'Activity.auditDetailTray.location',
    defaultMessage: 'Location',
    description: 'Label for the audit location detail row',
  },
  userAgent: {
    id: 'Activity.auditDetailTray.userAgent',
    defaultMessage: 'User agent',
    description: 'Label for the audit user agent detail row',
  },
  eventId: {
    id: 'Activity.auditDetailTray.eventId',
    defaultMessage: 'Event ID',
    description: 'Label for the audit event ID detail row',
  },
  copyEventId: {
    id: 'Activity.auditDetailTray.copyEventId',
    defaultMessage: 'Copy event ID',
    description: 'Accessibility label for the copy event ID button',
  },
  copyEventIdSuccess: {
    id: 'Activity.auditDetailTray.copyEventIdSuccess',
    defaultMessage: 'Copied to clipboard',
    description: 'Toast shown after the event ID is copied to the clipboard',
  },
});

type AuditDetailTrayProps = {
  auditLog: AuditLogRowEntry;
  onClose: () => void;
  currentPosition?: number;
  totalCount?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  isLoading?: boolean;
};

type AuditDetailBodyProps = {
  auditLog: AuditLogRowEntry;
};

function AuditDetailBody({ auditLog }: AuditDetailBodyProps) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { actor, actor_context: actorContext, target } = auditLog;

  const eventMeta = useMemo(() => getAuditEventMeta(auditLog.event_type), [auditLog.event_type]);
  const eventTitle = useMemo(
    () => (eventMeta ? formatMessage(eventMeta.message) : auditLog.event_type),
    [auditLog.event_type, eventMeta, formatMessage],
  );
  const eventIconName: IconName = eventMeta?.iconName ?? 'info';
  const actorName = actor.name || actor.id;
  const targetValue = target.email || target.name || target.id;
  const detailRows = useMemo<AuditLogDetailRowProps[]>(
    () => [
      { label: formatMessage(messages.action), value: eventTitle },
      { label: formatMessage(messages.target), value: targetValue },
    ],
    [eventTitle, formatMessage, targetValue],
  );
  const deviceRows = useMemo<AuditLogDetailRowProps[]>(
    () => [
      {
        label: formatMessage(messages.device),
        value: formatAuditLogDevice(actorContext.user_agent),
      },
      { label: formatMessage(messages.ip), value: actorContext.ip_address },
      { label: formatMessage(messages.location), value: formatAuditLogLocation(actorContext) },
      {
        label: formatMessage(messages.userAgent),
        value: formatAuditLogUserAgent(actorContext.user_agent),
      },
    ],
    [actorContext, formatMessage],
  );
  const handleCopyEventIdSuccess = useCallback(() => {
    logEvent('activity_event_id_copied', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });
    toast.show(formatMessage(messages.copyEventIdSuccess));
  }, [formatMessage, toast]);

  return (
    <>
      <AuditDetailHeader iconName={eventIconName} title={eventTitle} />
      <VStack width="100%" gap={3} paddingX={1} paddingTop={4} flexGrow={1}>
        <AuditLogActorSection
          name={actorName}
          email={actor.email ?? undefined}
          avatarUrl={actor.avatar_url || undefined}
        />
        <AuditLogDetailSection
          eventId={auditLog.event_id}
          title={formatMessage(messages.details)}
          rows={detailRows}
        />
        <AuditLogDetailSection
          eventId={auditLog.event_id}
          title={formatMessage(messages.deviceAndNetwork)}
          rows={deviceRows}
        />
      </VStack>
      <VStack width="100%" paddingX={1} paddingBottom={3}>
        <AuditLogDetailRow
          label={formatMessage(messages.eventId)}
          value={truncateEventId(auditLog.event_id)}
          copyValue={auditLog.event_id}
          copyAccessibilityLabel={formatMessage(messages.copyEventId)}
          onCopySuccess={handleCopyEventIdSuccess}
        />
      </VStack>
    </>
  );
}

export function AuditDetailTray({
  auditLog,
  onClose,
  currentPosition,
  totalCount,
  onPrevious,
  onNext,
  isLoading = false,
}: AuditDetailTrayProps) {
  const { formatMessage } = useIntl();
  const trayRef = useRef<TrayRefProps>(null);

  useEffect(() => {
    logEvent('activity_detail_tray_opened', {
      componentType: ComponentType.modal,
      action: ActionType.view,
    });
  }, []);

  const resultCount = useMemo(() => {
    if (currentPosition == null || totalCount == null) {
      return null;
    }

    return formatMessage(messages.resultCount, { currentPosition, totalCount });
  }, [currentPosition, formatMessage, totalCount]);

  const resultCountContent = useMemo(() => {
    if (isLoading) {
      return <Fallback width={80} height={16} testID="audit-detail-result-count-skeleton" />;
    }

    if (!resultCount) {
      return null;
    }

    return (
      <Text as="span" font="label2" color="fgMuted" overflow="truncate">
        {resultCount}
      </Text>
    );
  }, [isLoading, resultCount]);

  const handleClose = useCallback(() => {
    trayRef.current?.close();
  }, []);

  const handleCloseComplete = useCallback(() => {
    logEvent('activity_detail_tray_closed', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });
    onClose();
  }, [onClose]);

  return (
    <Tray
      ref={trayRef}
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      hideCloseButton
      onCloseComplete={handleCloseComplete}
      pin="right"
      styles={TRAY_STYLES}
    >
      <VStack width="100%" height="100%" gap={0}>
        <HStack
          width="100%"
          height={56}
          alignItems="flex-start"
          justifyContent="space-between"
          paddingTop={2}
          paddingX={3}
          paddingY={1.5}
        >
          <HStack alignItems="center" gap={2} width={250}>
            <HStack alignItems="center" gap={0}>
              <IconButton
                transparent
                disabled={!onPrevious}
                color="fg"
                name="caretLeft"
                onClick={onPrevious}
                accessibilityLabel={formatMessage(messages.previousAccessibilityLabel)}
                data-dd-action-name="activity_detail_prev"
              />
              <IconButton
                transparent
                disabled={!onNext}
                color="fg"
                name="caretRight"
                onClick={onNext}
                accessibilityLabel={formatMessage(messages.nextAccessibilityLabel)}
                data-dd-action-name="activity_detail_next"
              />
            </HStack>
            {resultCountContent}
          </HStack>
          <IconButton
            transparent
            color="fg"
            name="close"
            onClick={handleClose}
            accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
            data-dd-action-name="activity_detail_close"
          />
        </HStack>
        {isLoading ? <AuditDetailTraySkeleton /> : <AuditDetailBody auditLog={auditLog} />}
      </VStack>
    </Tray>
  );
}
